import { Component, ElementRef, EventEmitter, Input, Output, ViewChild, inject } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import {
  IssueFullTaxInvoiceFromFinanceResult,
  ParkingFinanceListRow
} from '../../../../../../core/models/finance/parking-finance.model';
import { ModalIssueFullTaxFromFinanceComponent } from '../modal-issue-full-tax-from-finance/modal-issue-full-tax-from-finance.component';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

@Component({
  selector: 'app-modal-parking-fine-receipt',
  standalone: true,
  imports: [CommonModule, ModalIssueFullTaxFromFinanceComponent],
  providers: [DatePipe],
  templateUrl: './modal-parking-fine-receipt.component.html',
  styleUrl: './modal-parking-fine-receipt.component.css'
})
export class ModalParkingFineReceiptComponent {
  @ViewChild('receiptRef', { static: false }) receiptRef!: ElementRef;

  private datePipe = inject(DatePipe);

  @Input() record: ParkingFinanceListRow | null = null;
  @Input() siteId = 0;
  @Input() userId = 0;
  @Input() menuId = 0;
  @Input() canIssueFullTax = false;

  @Output() close = new EventEmitter<void>();
  @Output() issued = new EventEmitter<IssueFullTaxInvoiceFromFinanceResult>();

  /** Controls PDF generation loading overlay */
  isGeneratingPdf = false;
  isIssueFullTaxModalOpen = false;

  // ================= Normal Functions =================

  /** Emits close signal */
  onClose(): void {
    this.close.emit();
  }

  openIssueFullTaxModal(): void {
    if (!this.canIssueFullTax || !this.record) return;
    this.isIssueFullTaxModalOpen = true;
  }

  closeIssueFullTaxModal(): void {
    this.isIssueFullTaxModalOpen = false;
  }

  onIssueFullTaxSuccess(result: IssueFullTaxInvoiceFromFinanceResult): void {
    this.isIssueFullTaxModalOpen = false;
    this.issued.emit(result);
  }

  /** Formats date into readable DD/MM/YYYY HH:mm string */
  formatDateDisplay(date: Date | string | null | undefined): string {
    if (!date) return '-';
    const d = date instanceof Date ? date : new Date(date);
    if (isNaN(d.getTime())) return '-';
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    return `${day}/${month}/${year} ${hh}:${mm}`;
  }

  /**
   * Converts any CSS color string (including oklch) to an sRGB hex string
   * by drawing it onto a temporary offscreen canvas.
   * This forces the browser to resolve modern color functions before html2canvas sees them.
   */
  private resolveColorToHex(color: string): string | null {
    try {
      const canvas = document.createElement('canvas');
      canvas.width = 1;
      canvas.height = 1;
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;
      ctx.fillStyle = color;
      ctx.fillRect(0, 0, 1, 1);
      const [r, g, b, a] = ctx.getImageData(0, 0, 1, 1).data;
      if (a === 0) return null; // transparent — skip
      return `rgb(${r},${g},${b})`;
    } catch {
      return null;
    }
  }

  /**
   * Inline-resolves all color CSS properties on an element by converting
   * any oklch() (or other modern color) values to plain rgb() via canvas.
   */
  private inlineResolvedColors(el: HTMLElement): void {
    const cs = window.getComputedStyle(el);
    const colorProps = [
      'color', 'background-color', 'border-color',
      'border-top-color', 'border-right-color',
      'border-bottom-color', 'border-left-color',
      'outline-color', 'text-decoration-color',
      'fill', 'stroke'
    ];
    colorProps.forEach(prop => {
      const value = cs.getPropertyValue(prop);
      if (!value || value === 'none' || value === 'transparent') return;
      const resolved = this.resolveColorToHex(value);
      if (resolved) {
        el.style.setProperty(prop, resolved, 'important');
      }
    });
  }

  /** Prints the receipt using an iframe to preserve styles */
  printReceipt(): void {
    setTimeout(() => {
      const element = this.receiptRef?.nativeElement;
      if (!element) return;

      const printContents = element.outerHTML;
      const iframe = document.createElement('iframe');
      iframe.style.position = 'absolute';
      iframe.style.top = '-10000px';
      iframe.style.left = '-10000px';
      iframe.style.width = '100%';
      iframe.style.height = '100%';
      document.body.appendChild(iframe);

      const iframeDoc = iframe.contentWindow?.document;
      if (!iframeDoc) return;

      const styles = Array.from(document.head.querySelectorAll('style, link[rel="stylesheet"]'))
        .map(s => s.outerHTML)
        .join('');

      iframeDoc.open();
      iframeDoc.write(`
        <html>
          <head>${styles}</head>
          <body>${printContents}</body>
        </html>
      `);
      iframeDoc.close();

      setTimeout(() => {
        iframe.contentWindow?.print();
      }, 500);
    }, 100);
  }

  /**
   * Downloads the receipt as a PDF.
   * Strategy: resolve all oklch/modern colors to rgb() inline on a cloned off-screen
   * node BEFORE passing to html2canvas, then remove all stylesheets from the clone
   * so html2canvas only sees plain inline rgb values.
   */
  downloadReceiptPdf(): void {
    const element = this.receiptRef?.nativeElement as HTMLElement;
    if (!element) return;

    // Show loading overlay
    this.isGeneratingPdf = true;

    // Delay 50ms so Angular can render the loading overlay
    // before pdf generation blocks the main thread
    setTimeout(async () => {
      try {
        // 1️⃣ Clone the receipt element into an off-screen div
        const wrapper = document.createElement('div');
        wrapper.style.position = 'fixed';
        wrapper.style.left = '-9999px';
        wrapper.style.top = '0';
        wrapper.style.width = element.offsetWidth + 'px';
        wrapper.style.backgroundColor = '#ffffff';
        document.body.appendChild(wrapper);

        const clone = element.cloneNode(true) as HTMLElement;
        wrapper.appendChild(clone);

        // 2️⃣ Walk every element in the clone and inline-resolve colors via canvas
        //    This converts oklch() → rgb() so html2canvas never sees oklch
        const allCloned = [clone, ...Array.from(clone.querySelectorAll('*'))] as HTMLElement[];

        // Mirror the computed styles from the live DOM elements into the clone first
        const allLive = [element, ...Array.from(element.querySelectorAll('*'))] as HTMLElement[];
        allCloned.forEach((clonedEl, i) => {
          if (i >= allLive.length) return;
          const liveEl = allLive[i];
          const cs = window.getComputedStyle(liveEl);

          // Copy font, layout, and box model properties (safe non-color props)
          const safeProps = [
            'font-size', 'font-weight', 'font-family', 'font-style',
            'line-height', 'letter-spacing', 'text-align', 'text-transform',
            'display', 'flex-direction', 'justify-content', 'align-items',
            'gap', 'padding', 'padding-top', 'padding-right', 'padding-bottom', 'padding-left',
            'margin', 'margin-top', 'margin-right', 'margin-bottom', 'margin-left',
            'width', 'height', 'min-width', 'min-height', 'max-width', 'max-height',
            'border-width', 'border-style', 'border-radius',
            'border-top-width', 'border-top-style',
            'border-bottom-width', 'border-bottom-style',
            'text-decoration', 'opacity', 'visibility', 'overflow',
            'white-space', 'word-break', 'word-wrap',
            'background-image', 'background-repeat'
          ];
          safeProps.forEach(prop => {
            const val = cs.getPropertyValue(prop);
            if (val) {
              try { clonedEl.style.setProperty(prop, val); } catch { /* skip */ }
            }
          });

          // Resolve all color properties via canvas (oklch → rgb)
          this.inlineResolvedColors(clonedEl);
        });

        // 3️⃣ Remove all <style> and <link rel="stylesheet"> from cloned scope
        //    so html2canvas only uses the inline styles we just set
        wrapper.querySelectorAll('style, link[rel="stylesheet"]').forEach(el => el.remove());

        // 4️⃣ Capture with html2canvas
        const canvas = await html2canvas(wrapper, {
          scale: 2,
          backgroundColor: '#ffffff',
          useCORS: true,
          logging: false,
        });

        document.body.removeChild(wrapper);

        // 5️⃣ Convert canvas → PDF
        const imgData = canvas.toDataURL('image/png');
        const pdfWidth = canvas.width * 0.264583;
        const pdfHeight = canvas.height * 0.264583;

        const pdf = new jsPDF({
          orientation: 'portrait',
          unit: 'mm',
          format: [pdfWidth, pdfHeight]
        });

        pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);

        const filename = `${this.record?.receiptId || 'receipt'}.pdf`;
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
        if (isIOS) {
          window.open(pdf.output('bloburl'));
        } else {
          pdf.save(filename);
        }
      } catch (error: any) {
        console.error('Error generating PDF:', error);
      } finally {
        // Always hide loading overlay
        this.isGeneratingPdf = false;
      }
    }, 50);
  }
}
